import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { ApiError, api } from "@/api/client";

/* Owner Finder v2 — paste an agency listing (URL or bare id), get back the
 * same property posted by its private owner, with phones to call. Reads the
 * live ssparser/mhparser scraper data via POST /api/owner-finder. */

/** A real number whose first six digits match a masked one. Inference —
 *  but every candidate is a number we already hold from a source that
 *  published it in full (ss.ge live, or the ss.ge archive). */
interface RecoveredPhone {
  number: string;
  /** How many listings we hold carrying this number, and how many of those
   *  were owner-posted. A number seen only on agency listings is probably
   *  the agency's, not the owner's. */
  listings: number;
  owner_listings: number;
  sources: string[];
}

interface ListingPhone {
  number: string;
  masked: boolean;
  is_main: boolean;
  has_viber: boolean;
  has_whatsapp: boolean;
  /** Only on masked numbers. */
  recovered?: RecoveredPhone[];
  /** high = one candidate · medium = two or three · low = crowded prefix,
   *  not worth guessing from · none = prefix unknown to us. */
  recovered_confidence?: "high" | "medium" | "low" | "none" | null;
  /** Distinct numbers sharing this prefix, before the display cut. */
  recovered_total?: number;
}

interface ParserListing {
  portal: "myhome" | "ss";
  external_id: string;
  url: string;
  title: string | null;
  seller_type: string | null;
  deal_type: string | null;
  real_estate_type: string | null;
  price_usd: number | null;
  price_native: number | null;
  area: number | null;
  rooms: number | null;
  floor: number | null;
  floors_total: number | null;
  city: string | null;
  district: string | null;
  subdistrict: string | null;
  street: string | null;
  street_number: string | null;
  address: string | null;
  cadastre: string | null;
  owner_name: string | null;
  phones: ListingPhone[];
  main_image: string | null;
  images: string[];
  image_count: number;
  listed_at: string | null;
  active: boolean;
  /** From the frozen hot_offers archive rather than a live portal — the
   *  listing is gone, the owner behind it isn't. */
  archived?: boolean;
}

interface OwnerMatch {
  score: number;
  reasons: string[];
  listing: ParserListing;
  twin: ParserListing | null;
}

interface OwnerLookupResponse {
  source: ParserListing | null;
  source_found: boolean;
  source_active: boolean;
  already_owner: boolean;
  candidates: OwnerMatch[];
  note: string | null;
}

const PORTAL_LABEL: Record<string, string> = { myhome: "MyHome", ss: "SS.ge" };

/* Staged "thinking" readout shown while the lookup runs — mirrors the real
 * pipeline phases so the wait reads as work, not lag. */
const SEARCH_STAGES: { label: string; hint: string }[] = [
  { label: "Resolving the listing", hint: "parsing the link · loading source data" },
  { label: "Checking cadastral codes", hint: "exact parcel match across portals" },
  { label: "Matching coordinates & location", hint: "≤500 m radius · district bridge" },
  { label: "Comparing price, area, rooms & floor", hint: "deal-type-aware price window" },
  { label: "Scoring candidates", hint: "price closeness dominates" },
  { label: "Pairing cross-portal twins", hint: "same posting on both portals" },
];
const STAGE_MS = 380;
const MIN_SEARCH_MS = SEARCH_STAGES.length * STAGE_MS + 350;

function detectPortal(q: string): string | null {
  const s = q.trim().toLowerCase();
  if (!s) return null;
  if (s.includes("myhome.ge")) return "MyHome";
  if (s.includes("ss.ge")) return "SS.ge";
  if (/^\d{5,12}$/.test(s)) return "ID";
  return null;
}

function fmtPrice(v: number | null): string {
  if (v == null) return "—";
  return "$" + Math.round(v).toLocaleString();
}

function fmtDate(s: string | null): string {
  if (!s) return "—";
  return new Date(s).toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

function perSqm(l: ParserListing): string | null {
  if (!l.price_usd || !l.area || l.area <= 0) return null;
  return "$" + Math.round(l.price_usd / l.area).toLocaleString() + "/m²";
}

function locationLine(l: ParserListing): string {
  const parts = [
    l.district,
    l.subdistrict,
    l.street ? `${l.street}${l.street_number ? " " + l.street_number : ""}` : l.address,
  ].filter(Boolean);
  return parts.join(" · ") || l.city || "—";
}

export function OwnerFinder() {
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<OwnerLookupResponse | null>(null);
  const resultRef = useRef<HTMLDivElement | null>(null);

  const detected = useMemo(() => detectPortal(query), [query]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (query.trim().length < 5 || busy) return;
    setBusy(true);
    setErr(null);
    setResult(null);
    const started = Date.now();
    try {
      const r = await api<OwnerLookupResponse>("/api/owner-finder", {
        method: "POST",
        json: { query: query.trim() },
      });
      // Let the thinking modal play its stages even on fast lookups.
      const wait = MIN_SEARCH_MS - (Date.now() - started);
      if (wait > 0) await new Promise((res) => setTimeout(res, wait));
      setResult(r);
      setTimeout(
        () => resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }),
        60,
      );
    } catch (error) {
      if (error instanceof ApiError && error.status === 503) {
        setErr("The listings scraper isn't reachable right now — try again in a minute.");
      } else {
        setErr(error instanceof Error ? error.message : "Lookup failed");
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="w-full space-y-4">
      {/* Input hero */}
      <div className="card overflow-hidden border border-white/5 bg-gradient-to-br from-accent-500/[0.07] via-transparent to-transparent p-6">
        <h1 className="text-xl font-semibold tracking-[-0.01em] text-slate-100">
          Owner finder
        </h1>
        <p className="mt-1 text-[12.5px] text-slate-500 max-w-2xl">
          Paste an agency-posted listing — we'll search the live scraper data on
          both portals for the same property posted by its owner, with a phone
          number to call.
        </p>
        <form onSubmit={onSubmit} className="mt-4 flex flex-col sm:flex-row gap-2 max-w-3xl">
          <div className="relative flex-1 min-w-0">
            <input
              className="input !py-3 !text-[14px] !pr-20"
              placeholder="MyHome or SS.ge listing URL — or a bare listing ID"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              disabled={busy}
              autoFocus
            />
            {detected && (
              <span className="absolute right-3 top-1/2 -translate-y-1/2 badge border-accent-500/30 bg-accent-500/10 text-accent-300 whitespace-nowrap">
                {detected}
              </span>
            )}
          </div>
          <button
            type="submit"
            className="btn-primary !px-6 !py-3 flex-shrink-0"
            disabled={busy || query.trim().length < 5}
          >
            {busy ? "Searching…" : "Find owner"}
          </button>
        </form>
        <div className="mt-2.5 text-[11px] text-slate-600">
          Searches both portals plus our own archive · MyHome numbers arrive masked from
          the portal — when the property is on both portals we show both.
        </div>
      </div>

      {busy && <SearchingModal />}

      {err && (
        <div className="max-w-3xl card p-4 border-rose-500/25 bg-rose-500/[0.04] text-rose-300 text-sm">
          {err}
        </div>
      )}

      {result && !busy && (
        <div ref={resultRef} className="max-w-3xl space-y-4">
          <VerdictBanner result={result} />

          {result.source && (
            <div>
              <div className="flex items-center justify-between px-1 mb-2">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                  Source listing
                </span>
                {!result.source_active && (
                  <span className="badge border-amber-500/30 bg-amber-500/10 text-amber-300">
                    removed from portal
                  </span>
                )}
              </div>
              <div className="card overflow-hidden">
                <ListingCard listing={result.source} />
              </div>
            </div>
          )}

          {result.candidates.length > 0 && (
            <div className="space-y-3">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 px-1">
                Owner matches · {result.candidates.length}
              </div>
              {result.candidates.map((m) => (
                <OwnerMatchCard
                  key={`${m.listing.portal}-${m.listing.external_id}`}
                  match={m}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Thinking modal ──────────────────────────────────────────────────────── */

function SearchingModal() {
  const [stage, setStage] = useState(0);
  useEffect(() => {
    const t = setInterval(
      () => setStage((s) => Math.min(s + 1, SEARCH_STAGES.length - 1)),
      STAGE_MS,
    );
    return () => clearInterval(t);
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/65">
      <div className="card w-full max-w-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-white/5 flex items-center gap-3">
          <span className="relative flex h-2.5 w-2.5 flex-shrink-0">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent-400 opacity-60" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-accent-500" />
          </span>
          <div className="text-[14px] font-semibold text-slate-100">
            Hunting the owner…
          </div>
        </div>
        <div className="px-5 py-4 space-y-2.5">
          {SEARCH_STAGES.map((s, i) => {
            const done = i < stage;
            const active = i === stage;
            return (
              <div
                key={s.label}
                className={`flex items-start gap-2.5 transition-opacity ${
                  done || active ? "opacity-100" : "opacity-35"
                }`}
              >
                <span className="mt-0.5 h-4 w-4 flex-shrink-0 flex items-center justify-center">
                  {done ? (
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="text-emerald-400">
                      <path d="M20 6 9 17l-5-5" />
                    </svg>
                  ) : active ? (
                    <span className="h-3 w-3 rounded-full border-2 border-accent-400 border-t-transparent animate-spin" />
                  ) : (
                    <span className="h-1.5 w-1.5 rounded-full bg-slate-600" />
                  )}
                </span>
                <div className="min-w-0">
                  <div
                    className={`text-[12.5px] leading-tight ${
                      done ? "text-slate-400" : active ? "text-slate-100 font-medium" : "text-slate-500"
                    }`}
                  >
                    {s.label}
                  </div>
                  {active && (
                    <div className="text-[10.5px] text-slate-500 mt-0.5">{s.hint}</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ── Verdict banner ──────────────────────────────────────────────────────── */

function VerdictBanner({ result }: { result: OwnerLookupResponse }) {
  if (!result.source_found) {
    return (
      <div className="card p-4 border-amber-500/25 bg-amber-500/[0.05] text-amber-300 text-[13px]">
        {result.note || "Listing not found in the scraper data."}
      </div>
    );
  }
  if (result.already_owner) {
    return (
      <div className="card p-4 border-accent-500/30 bg-accent-500/[0.06] text-accent-300 text-[13px] font-medium">
        ✓ {result.note || "This listing is already posted by the owner."}
      </div>
    );
  }
  if (result.candidates.length > 0) {
    const top = result.candidates[0];
    return (
      <div className="card p-4 border-emerald-500/25 bg-emerald-500/[0.05] text-emerald-300 text-[13px] font-medium">
        ✓ Owner match found — top score {top.score}%
        {result.note && (
          <span className="block mt-1 text-[12px] font-normal text-emerald-300/70">
            {result.note}
          </span>
        )}
      </div>
    );
  }
  return (
    <div className="card p-4 border-white/10 bg-white/[0.02] text-slate-400 text-[13px]">
      {result.note || "No owner-posted match found."}
    </div>
  );
}

/* ── Photo gallery with arrows ───────────────────────────────────────────── */

function PhotoGallery({ listing }: { listing: ParserListing }) {
  const photos =
    listing.images.length > 0
      ? listing.images
      : listing.main_image
        ? [listing.main_image]
        : [];
  const [idx, setIdx] = useState(0);
  const n = photos.length;
  const step = (d: number) => setIdx((i) => (i + d + n) % n);

  if (n === 0) {
    return (
      <div className="h-52 w-full bg-white/[0.04] flex items-center justify-center text-slate-700 text-[11px]">
        no photos
      </div>
    );
  }
  return (
    <div className="relative group/gal h-52 w-full bg-black/30 overflow-hidden">
      <img
        src={photos[idx]}
        alt=""
        className="h-full w-full object-cover"
        loading="lazy"
      />
      {n > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); step(-1); }}
            className="absolute left-1.5 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full bg-black/55 text-white/90 hover:bg-black/75 flex items-center justify-center opacity-0 group-hover/gal:opacity-100 transition-opacity"
            aria-label="Previous photo"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6" /></svg>
          </button>
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); step(1); }}
            className="absolute right-1.5 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full bg-black/55 text-white/90 hover:bg-black/75 flex items-center justify-center opacity-0 group-hover/gal:opacity-100 transition-opacity"
            aria-label="Next photo"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" /></svg>
          </button>
          <span className="absolute bottom-1.5 right-1.5 rounded-md bg-black/60 px-1.5 py-0.5 text-[10px] font-medium tabular-nums text-white/90">
            {idx + 1}/{n}
          </span>
        </>
      )}
    </div>
  );
}

/* ── Cards ───────────────────────────────────────────────────────────────── */

function ScoreBadge({ score }: { score: number }) {
  const tone =
    score >= 90
      ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
      : score >= 70
        ? "border-accent-500/30 bg-accent-500/10 text-accent-300"
        : "border-slate-500/30 bg-slate-500/10 text-slate-300";
  return (
    <span className={`badge ${tone} !text-[11px] font-semibold tabular-nums`}>
      {score}% match
    </span>
  );
}

function PortalBadgePill({ portal }: { portal: string }) {
  return (
    <span
      className={
        "badge whitespace-nowrap " +
        (portal === "myhome"
          ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
          : "border-sky-500/30 bg-sky-500/10 text-sky-300")
      }
    >
      {PORTAL_LABEL[portal] ?? portal}
    </span>
  );
}

function PhoneChips({ listing }: { listing: ParserListing }) {
  if (listing.phones.length === 0) {
    return <span className="text-[11.5px] text-slate-600">no phone in data</span>;
  }
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {listing.phones.slice(0, 2).map((p) =>
        p.masked ? (
          <span key={p.number} className="inline-flex items-center gap-1.5 flex-wrap">
            <span
              className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/[0.03] px-2.5 py-1 text-[12.5px] font-mono tabular-nums text-slate-400"
              title="Masked by the portal"
            >
              {p.number}
              <span className="text-[9px] uppercase tracking-wider text-slate-600">
                masked
              </span>
            </span>
            {/* MyHome hides the last three digits, which makes the number
                useless on its own. These are the real numbers we hold whose
                visible prefix matches — dashed, never solid, because it is
                an inference and the user should see that at a glance. */}
            {(p.recovered ?? []).map((rc) => (
              <a
                key={rc.number}
                href={`tel:+995${rc.number}`}
                className={`inline-flex items-center gap-1.5 rounded-lg border border-dashed px-2.5 py-1 text-[12.5px] font-mono tabular-nums transition-colors ${
                  p.recovered_confidence === "high"
                    ? "border-amber-400/40 bg-amber-500/10 text-amber-200 hover:bg-amber-500/20"
                    : "border-white/15 bg-white/[0.03] text-slate-300 hover:bg-white/[0.07]"
                }`}
                title={
                  `Not published by the portal — matched on the ${p.number.replace(/\D/g, "").slice(0, 6)} prefix. ` +
                  `Seen on ${rc.listings} listing${rc.listings === 1 ? "" : "s"}` +
                  (rc.owner_listings ? `, ${rc.owner_listings} owner-posted` : "") +
                  `. ${p.recovered_total} number${p.recovered_total === 1 ? "" : "s"} share this prefix.`
                }
              >
                {rc.number}
                <span className="text-[9px] uppercase tracking-wider opacity-70">
                  {p.recovered_confidence === "high" ? "likely" : "maybe"}
                </span>
              </a>
            ))}
            {!p.recovered?.length && p.recovered_confidence === "low" && (
              <span className="text-[10.5px] text-slate-600" title={`${p.recovered_total} numbers share this prefix — too many to guess from.`}>
                {p.recovered_total} numbers share this prefix
              </span>
            )}
          </span>
        ) : (
          <a
            key={p.number}
            href={`tel:+995${p.number}`}
            className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-500/25 bg-emerald-500/10 px-2.5 py-1 text-[12.5px] font-mono tabular-nums text-emerald-300 hover:bg-emerald-500/20 transition-colors"
            title="Call"
          >
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.8 19.8 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.08 4.18 2 2 0 0 1 4.06 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
            </svg>
            {p.number}
            {p.has_viber && <span className="text-[9px] text-violet-300/80">V</span>}
            {p.has_whatsapp && <span className="text-[9px] text-emerald-300/80">W</span>}
          </a>
        ),
      )}
      {listing.owner_name && (
        <span className="text-[11.5px] text-slate-500 truncate max-w-[10rem]" title={listing.owner_name}>
          {listing.owner_name}
        </span>
      )}
    </div>
  );
}

function AttrCell({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <div className="text-[9.5px] uppercase tracking-wider text-slate-600 font-semibold">
        {label}
      </div>
      <div className="text-[12.5px] font-medium tabular-nums text-slate-200 truncate">
        {value}
      </div>
    </div>
  );
}

/** One listing, gallery left · facts right. Lives inside a `card` shell. */
function ListingCard({ listing }: { listing: ParserListing }) {
  return (
    <div className="flex flex-col sm:flex-row">
      <div className="sm:w-64 flex-shrink-0">
        <PhotoGallery listing={listing} />
      </div>
      <div className="flex-1 min-w-0 px-4 py-3.5 flex flex-col gap-2">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="text-[19px] font-semibold tabular-nums text-slate-100 leading-none">
            {fmtPrice(listing.price_usd)}
          </span>
          {perSqm(listing) && (
            <span className="text-[11px] text-slate-500 tabular-nums">
              {perSqm(listing)}
            </span>
          )}
          <span className="ml-auto flex items-center gap-1.5">
            {/* An archived row is no longer on the portal — its link will
                404. Say so, because the phone is still the point. */}
            {listing.archived && (
              <span
                className="badge whitespace-nowrap border-amber-500/30 bg-amber-500/10 text-amber-300"
                title="From our archive — this listing is no longer on the portal, but it's evidence of who owns the property."
              >
                archived
              </span>
            )}
            <PortalBadgePill portal={listing.portal} />
          </span>
        </div>

        <div className="text-[11.5px] text-slate-500">
          {[listing.deal_type, listing.real_estate_type].filter(Boolean).join(" · ")}
          <span className="text-slate-600"> · listed {fmtDate(listing.listed_at)}</span>
        </div>

        <div className="grid grid-cols-3 gap-2 py-1.5 border-y border-white/5">
          <AttrCell label="Area" value={listing.area ? `${listing.area} m²` : "—"} />
          <AttrCell label="Rooms" value={listing.rooms != null ? String(listing.rooms) : "—"} />
          <AttrCell
            label="Floor"
            value={
              listing.floor != null
                ? `${listing.floor}${listing.floors_total ? " / " + listing.floors_total : ""}`
                : "—"
            }
          />
        </div>

        <div className="text-[11.5px] text-slate-400 truncate" title={locationLine(listing)}>
          {locationLine(listing)}
        </div>
        {listing.cadastre && (
          <div className="text-[10.5px] text-slate-600 font-mono truncate" title="Cadastral code">
            ⌗ {listing.cadastre}
          </div>
        )}

        <div className="mt-auto flex items-center justify-between gap-2 flex-wrap">
          <PhoneChips listing={listing} />
          <a
            href={listing.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[11.5px] font-medium text-accent-400 hover:text-accent-300 whitespace-nowrap"
          >
            Open on {PORTAL_LABEL[listing.portal]} ↗
          </a>
        </div>
      </div>
    </div>
  );
}

function OwnerMatchCard({ match }: { match: OwnerMatch }) {
  const l = match.listing;
  return (
    <div className="card overflow-hidden border border-white/5">
      <div className="px-4 py-2.5 border-b border-white/5 flex items-center gap-2 flex-wrap bg-white/[0.015]">
        <ScoreBadge score={match.score} />
        {match.reasons.map((r) => (
          <span
            key={r}
            className="text-[10.5px] px-2 py-0.5 rounded-full border border-white/10 bg-white/[0.02] text-slate-400 whitespace-nowrap"
          >
            {r}
          </span>
        ))}
        {!l.active && (
          <span className="badge border-amber-500/30 bg-amber-500/10 text-amber-300 ml-auto">
            removed
          </span>
        )}
      </div>
      <ListingCard listing={l} />
      {match.twin && (
        <div className="border-t border-white/5 bg-white/[0.015]">
          <div className="px-4 pt-2.5 text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">
            Also posted on {PORTAL_LABEL[match.twin.portal]}
          </div>
          <ListingCard listing={match.twin} />
        </div>
      )}
    </div>
  );
}
